"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { Compass, ExternalLink, MapPin } from "lucide-react";
import { routes } from "@/lib/routes";

const GOOGLE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Eduardo+Sfeir+2827+El+Bolsón+Río+Negro+Argentina";

export default function HowToGetTherePage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-surface-muted pb-20 sm:pb-28">
      {/* Hero — layered imagery + type (aligned with pricing / pitches) */}
      <section className="relative flex min-h-[280px] flex-col items-center justify-center overflow-hidden px-4 pb-20 pt-12 sm:min-h-[320px] sm:pb-24 sm:pt-14 md:min-h-[360px]">
        <div className="absolute inset-0 bg-slate-950" aria-hidden />
        <Image
          src="/new/DSC08873.JPG"
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-[center_42%] opacity-[0.38]"
          quality={85}
        />
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_95%_88%_at_50%_12%,transparent_0%,rgba(15,23,42,0.9)_55%,rgb(15,23,42)_100%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-linear-to-b from-slate-950/35 via-slate-950/72 to-slate-950"
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-[0.35] mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }}
          aria-hidden
        />

        <div className="relative z-10 mx-auto w-full max-w-3xl px-2 text-center">
          <p className="mx-auto mb-5 inline-flex max-w-2xl items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[13px] font-medium leading-snug text-white/95 shadow-sm backdrop-blur-md sm:text-sm">
            {t("howToGetThere.subtitle")}
          </p>
          <h1 className="text-balance text-[1.7rem] font-semibold tracking-[-0.02em] text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)] sm:text-4xl md:text-5xl">
            {t("howToGetThere.title")}
          </h1>
          <nav
            className="mt-5 flex flex-wrap items-center justify-center gap-2 text-[13px] font-medium text-slate-400"
            aria-label="Breadcrumb"
          >
            <Link href={routes.home} className="transition-colors hover:text-white">
              {t("contact.breadcrumbHome")}
            </Link>
            <span className="text-slate-600" aria-hidden>
              —
            </span>
            <span className="text-slate-200">{t("howToGetThere.title")}</span>
          </nav>
        </div>
      </section>

      {/* Overlapping panel: address + map */}
      <div className="relative z-20 mx-auto -mt-14 w-full max-w-5xl px-4 sm:-mt-16 sm:px-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(15,23,42,0.14)] ring-1 ring-slate-900/5">
          <div className="grid lg:grid-cols-12 lg:items-stretch">
            <div className="border-b border-slate-100 bg-linear-to-b from-white to-slate-50/80 p-8 sm:p-10 lg:col-span-5 lg:border-b-0 lg:border-r lg:border-slate-100">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-linear-to-br from-primary/12 to-primary/5 text-primary shadow-inner ring-1 ring-primary/10">
                <MapPin className="h-7 w-7" strokeWidth={2} aria-hidden />
              </div>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                {t("footer.ourLocation")}
              </p>
              <p className="mt-2 text-pretty text-xl font-semibold leading-snug tracking-tight text-slate-900 sm:text-[1.35rem]">
                {t("howToGetThere.address")}
              </p>
              <div className="mt-5 flex gap-3 rounded-xl border border-slate-100/90 bg-white/80 p-4 shadow-sm">
                <Compass
                  className="mt-0.5 h-5 w-5 shrink-0 text-primary/80"
                  strokeWidth={2}
                  aria-hidden
                />
                <p className="text-sm leading-relaxed text-slate-600">{t("howToGetThere.landmark")}</p>
              </div>
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_-4px_rgba(34,72,214,0.55)] transition hover:bg-primary-strong sm:w-auto"
              >
                <ExternalLink className="h-4 w-4 opacity-95" aria-hidden />
                {t("contact.openInMaps")}
              </a>
            </div>

            <div className="relative min-h-[280px] bg-slate-100/90 lg:col-span-7 lg:h-full lg:min-h-0">
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block min-h-[280px] w-full overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary lg:absolute lg:inset-0 lg:min-h-0 lg:h-full"
              >
                <Image
                  src="/big-sports-location-map.png"
                  alt={t("images.howToGetThereMap")}
                  fill
                  className="object-cover object-center transition duration-500 group-hover:scale-[1.02]"
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  priority
                />
                <div
                  className="absolute inset-0 bg-linear-to-t from-slate-950/55 via-slate-950/10 to-transparent opacity-90 transition group-hover:from-slate-950/65"
                  aria-hidden
                />
                <div className="absolute bottom-0 left-0 right-0 flex flex-wrap items-end justify-between gap-3 p-5 sm:p-6">
                  <p className="max-w-[min(100%,22rem)] text-left text-sm font-medium leading-snug text-white drop-shadow-md">
                    {t("canchaPitchPage.cityRegion")}
                  </p>
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition group-hover:bg-white/25">
                    {t("contact.openInMaps")}
                    <ExternalLink className="h-3.5 w-3.5 opacity-90" aria-hidden />
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
